import { Reservations } from '@lib/state/api';
import { from } from 'rxjs';

import { map } from 'rxjs/operators';

import { saveAs } from 'file-saver';

import { dispatchForm, MediaTypes, CommonForm, PaginationState } from '@lib/state';
import { BillingFrequencyType, CorporateAccount } from '../corporate-account';
import {
  CorporateInvoice,
  InvoiceType,
  RegenerateInvoicesRequest,
} from './corporate-invoice.model';
import { corporateInvoiceStore, CorporateInvoiceStore } from './corporate-invoice.store';
import { CorporateInvoiceForms } from './corporate-invoice.forms';
import { invoiceRunStore, InvoiceRunStore } from '../invoice-run';
import { applyTransaction } from '@datorama/akita';
import { invoiceStatusStore, InvoiceStatusStore } from '../invoice-status';

export class CorporateInvoiceService {
  constructor(
    private readonly store: CorporateInvoiceStore,
    private readonly irStore: InvoiceRunStore,
    private readonly isStore: InvoiceStatusStore,
    private readonly corporateAccountApi: Reservations.CorporateAccountApi
  ) {}

  getInvoices(
    corporateAccountId: CorporateAccount['id'],
    continuationToken?: string | null,
    limit?: number
  ) {
    from(
      this.corporateAccountApi.corporateaccountIdInvoiceGet(
        corporateAccountId,
        continuationToken ?? undefined,
        limit
      )
    )
      .pipe(map(response => response.data))
      .subscribe(({ data, ...pagination }) => {
        this.store.upsertMany(data);
        this.updatePaginationState(pagination);
      });
  }

  createInvoice(
    corporateAccountId: CorporateAccount['id'],
    startDate: string,
    endDate: string,
    type: InvoiceType,
    isDraft = false,
    billingFrequencyType?: BillingFrequencyType
  ) {
    from(
      this.corporateAccountApi.corporateaccountInvoicePost({
        corporateAccounts: [corporateAccountId],
        startDate,
        endDate,
        isDraft,
        type,
        billingFrequencyType,
      })
    )
      .pipe(
        map(({ data }) => data.data),
        dispatchForm(CorporateInvoiceForms.Create)
      )
      .subscribe(x =>
        applyTransaction(() => {
          this.irStore.upsert(x.id, x);
          this.irStore.setActive(x.id);
          this.isStore.reset();
        })
      );
  }

  regenerateInvoice(request: RegenerateInvoicesRequest) {
    from(this.corporateAccountApi.corporateaccountInvoiceRegeneratePost(request))
      .pipe(
        map(({ data }) => data.data),
        dispatchForm(CorporateInvoiceForms.Regenerate)
      )
      .subscribe(data => {
        this.irStore.upsert(data.id, data);
      });
  }

  createMultipleInvoices(
    corporateAccounts: CorporateAccount[],
    startDate: string,
    endDate: string,
    type: InvoiceType,
    isDraft = false,
    billingFrequencyType?: BillingFrequencyType
  ) {
    from(
      this.corporateAccountApi.corporateaccountInvoicePost({
        corporateAccounts: corporateAccounts.map(a => a.id),
        startDate,
        endDate,
        isDraft,
        type,
        billingFrequencyType,
      })
    )
      .pipe(
        map(({ data }) => data.data),
        dispatchForm(CorporateInvoiceForms.Create)
      )
      .subscribe(x => {
        this.irStore.upsert(x.id, x);
        this.irStore.setActive(x.id);
        this.isStore.reset();
      });
  }

  removeInvoice({ id, corporateAccountId }: CorporateInvoice) {
    from(this.corporateAccountApi.corporateaccountIdInvoiceInvoiceIdDelete(corporateAccountId, id))
      .pipe(dispatchForm(CorporateInvoiceForms.Remove))
      .subscribe(() => this.store.remove(id));
  }

  resendInvoice({ id, corporateAccountId }: CorporateInvoice) {
    from(
      this.corporateAccountApi.corporateaccountIdInvoiceInvoiceIdResendGet(corporateAccountId, id)
    )
      .pipe(dispatchForm(CorporateInvoiceForms.Resend))
      .subscribe();
  }

  downloadInvoice(
    { id, invoiceNumber, exportName, corporateAccountId }: CorporateInvoice,
    account: CorporateAccount
  ) {
    from(
      this.corporateAccountApi.corporateaccountIdInvoiceInvoiceIdGet(corporateAccountId, id, {
        responseType: 'blob',
      })
    )
      .pipe(
        map(x => (x.status === 200 ? new Blob([x.data], { type: MediaTypes.ExcelFile }) : null)),
        dispatchForm(CommonForm.Export, true)
      )
      .subscribe(x => {
        if (x) {
          const fileName = (exportName ?? `${invoiceNumber} ${account.name}`).replace(
            /[<>:"/\\|?*]/g,
            '-'
          );
          saveAs(x, `${fileName}.xlsx`);
        }
      });
  }

  private updatePaginationState({ isDone, continuationToken }: PaginationState) {
    this.store.update(() => ({
      pagination: { isDone, continuationToken },
    }));
  }
}

export const corporateInvoiceService = new CorporateInvoiceService(
  corporateInvoiceStore,
  invoiceRunStore,
  invoiceStatusStore,
  new Reservations.CorporateAccountApi()
);
